import {
  CheckIcon,
  Loader2Icon,
  RotateCwIcon,
  SparklesIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/utils'
import type {
  ReportProgress,
  ReportProgressPhase,
  ReportProgressSection,
} from '../stores/types'

interface GenerationProgressPanelProps {
  progress: ReportProgress
}

const PHASES: { key: ReportProgressPhase; label: string }[] = [
  { key: 'reviewing', label: 'Reviewing case' },
  { key: 'analyzing', label: 'Analyzing transactions' },
  { key: 'writing_sections', label: 'Writing sections' },
  { key: 'finalizing', label: 'Finalizing' },
]

const STALE_AFTER_MS = 2 * 60 * 1000
const STALE_CHECK_INTERVAL_MS = 15_000

const currentPhaseIndex = (phase?: ReportProgressPhase) => {
  if (phase === 'delivered') return PHASES.length
  const index = PHASES.findIndex((entry) => entry.key === phase)
  return index === -1 ? 0 : index
}

// No fresh snapshot for a while: reassure instead of looking frozen.
const useStaleness = (seq?: number) => {
  const [lastChangeAt, setLastChangeAt] = useState(() => Date.now())
  const [isStale, setIsStale] = useState(false)

  useEffect(() => {
    setLastChangeAt(Date.now())
    setIsStale(false)
  }, [seq])

  useEffect(() => {
    const interval = setInterval(() => {
      setIsStale(Date.now() - lastChangeAt > STALE_AFTER_MS)
    }, STALE_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [lastChangeAt])

  return isStale
}

export default function GenerationProgressPanel({
  progress,
}: GenerationProgressPanelProps) {
  const phaseIndex = currentPhaseIndex(progress.phase)
  const isStale = useStaleness(progress.seq)
  const sections = progress.sections ?? []

  return (
    <div className="max-w-lg mx-auto py-16 flex flex-col items-center gap-8">
      <div className="relative mx-auto h-14 w-14">
        <div className="absolute inset-0 rounded-full bg-brand/10 animate-ping [animation-duration:2s]" />
        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-brand/10">
          <SparklesIcon className="h-6 w-6 text-brand animate-pulse" />
        </div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Daystrom is on the case</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          This typically takes 5-10 minutes. You can leave this page and come
          back. The report will be waiting for you.
        </p>
      </div>

      <PhaseTracker phaseIndex={phaseIndex} />

      {progress.phase === 'analyzing' && (
        <AnalysisProgress progress={progress} />
      )}

      {sections.length > 0 && <SectionChecklist sections={sections} />}

      <div className="min-h-6 text-center space-y-1">
        {progress.label && (
          <p className="text-sm text-muted-foreground italic">
            {progress.label}
          </p>
        )}
        {isStale && (
          <p className="text-xs text-muted-foreground">
            Still working. Long steps can take a few minutes.
          </p>
        )}
      </div>
    </div>
  )
}

const PhaseTracker = ({ phaseIndex }: { phaseIndex: number }) => (
  <nav aria-label="Report progress" className="flex items-center w-full">
    {PHASES.map((phase, index) => {
      const isActive = index === phaseIndex
      const isCompleted = index < phaseIndex

      return (
        <div
          key={phase.key}
          className="flex items-center flex-1 last:flex-none"
        >
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full transition-all',
                (isActive || isCompleted) && 'bg-brand text-white',
                !isActive &&
                  !isCompleted &&
                  'border border-border text-muted-foreground',
              )}
            >
              {isCompleted ? (
                <CheckIcon className="h-3 w-3" />
              ) : isActive ? (
                <Loader2Icon className="h-3 w-3 animate-spin" />
              ) : (
                <span className="text-[10px] font-semibold">{index + 1}</span>
              )}
            </div>
            <span
              className={cn(
                'text-xs font-medium hidden sm:inline whitespace-nowrap',
                isActive && 'text-brand',
                isCompleted && 'text-foreground',
                !isActive && !isCompleted && 'text-muted-foreground',
              )}
            >
              {phase.label}
            </span>
          </div>

          {index < PHASES.length - 1 && (
            <div className="flex-1 mx-2.5">
              <div
                className={cn(
                  'h-px w-full transition-colors',
                  isCompleted ? 'bg-brand' : 'bg-border',
                )}
              />
            </div>
          )}
        </div>
      )
    })}
  </nav>
)

const AnalysisProgress = ({ progress }: { progress: ReportProgress }) => {
  const done = progress.analysis?.chunks_done ?? 0
  const total = progress.analysis?.chunks_total ?? 0
  const scan = progress.analysis?.undisclosed_scan ?? 'pending'

  return (
    <div className="w-full space-y-3">
      {total > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Reading every transaction
            </span>
            <span className="font-medium tabular-nums">
              {done} of {total} batches
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-brand transition-all duration-500"
              style={{ width: `${Math.round((done / total) * 100)}%` }}
            />
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {scan === 'done' ? (
          <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
        ) : scan === 'running' ? (
          <Loader2Icon className="h-3.5 w-3.5 animate-spin text-brand" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-border mx-1" />
        )}
        <span>Checking for undisclosed accounts</span>
      </div>
    </div>
  )
}

const SECTION_STATUS_COPY: Record<string, string> = {
  writing: 'Writing',
  written: 'Done',
  retrying: 'Retrying',
}

const SectionChecklist = ({
  sections,
}: {
  sections: ReportProgressSection[]
}) => (
  <ul className="w-full rounded-lg border border-border divide-y divide-border">
    {sections.map((section) => {
      const status = section.status ?? 'pending'
      return (
        <li
          key={section.id ?? section.title}
          className="flex items-center gap-2.5 px-3.5 py-2 text-sm"
        >
          <SectionStatusIcon status={status} />
          <span
            className={cn(
              'flex-1 truncate',
              status === 'pending'
                ? 'text-muted-foreground'
                : 'text-foreground',
            )}
          >
            {section.title ?? section.id}
          </span>
          {SECTION_STATUS_COPY[status] && (
            <span
              className={cn(
                'text-xs',
                status === 'written' && 'text-emerald-600',
                status === 'writing' && 'text-brand',
                status === 'retrying' && 'text-amber-600',
              )}
            >
              {SECTION_STATUS_COPY[status]}
            </span>
          )}
        </li>
      )
    })}
  </ul>
)

const SectionStatusIcon = ({ status }: { status: string }) => {
  if (status === 'written')
    return <CheckIcon className="h-4 w-4 text-emerald-600 shrink-0" />
  if (status === 'writing')
    return <Loader2Icon className="h-4 w-4 animate-spin text-brand shrink-0" />
  if (status === 'retrying')
    return (
      <RotateCwIcon className="h-4 w-4 animate-spin text-amber-600 shrink-0" />
    )
  return (
    <span className="flex h-4 w-4 items-center justify-center shrink-0">
      <span className="h-1.5 w-1.5 rounded-full bg-border" />
    </span>
  )
}
